import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { differenceInMinutes } from "date-fns";
import { toZonedTime } from 'date-fns-tz';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography,   
    CircularProgress, 
    TextField, 
    Button,  
    MenuItem, 
    Select, 
    FormControl, 
    InputLabel,
    Autocomplete, 
} from '@mui/material';
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { de, enGB } from 'date-fns/locale';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { formatNumber } from 'state/helpers';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import CustomDateTimePicker from "components/CustomDateTimePicker";


const TravelAuthorizationFormPage = () => {
    useAuthCheck();
    const { travelAuthorizationFormId } = useParams();    
    const navigate = useNavigate();
    const locales = { 'en-us': undefined, 'en-gb': enGB, de };
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [airports, setAirports] = useState([]);
    const [crewMembers, setCrewMembers] = useState([]);    
    const [travelAllowances, setTravelAllowances] = useState([]);
    
    const parseUTCDateTime = (datetime) => {
        if (!datetime) return null;
        const date = new Date(datetime);
        return new Date(Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            date.getMinutes()
        ));
    };

    const calcTravelHours = (startDate, endDate) => {
        if (!startDate || !endDate) return 0;
        const minutes = differenceInMinutes(new Date(endDate), new Date(startDate));
        return (minutes / 60);
    };

    const [formValues, setFormValues] = useState({
        crewCode: "",        
        crewName: "",
        jobGradeCode: "",
        startDate: new Date(),
        endDate: new Date(),
        travelHours: 0,
        destination: "",  
        travelZone: "",
        allowanceUnit: 0,
        allowanceAmount: 0,
        currency: 'VND',
        description: "",  
        status: "approved",  
    });
    
    
    const fetchAirports = useCallback(async () => {
        return await api.get('/airports/airportswithtravelzone');
    }, []);
    
    const fetchCrewMembers = useCallback(async () => {
        return await api.get('/crewmembers');
    }, []);

    const fetchTravelAllowances = useCallback(async () => {
        return await api.get('/travelallowances/active');
    }, []);

    const fetchTravelAuthorizationForm = useCallback(async () => {
        if (travelAuthorizationFormId) {
            const url = `/travelauthorizationforms/${travelAuthorizationFormId}`;
            return await api.get(url);            
        }
    }, [travelAuthorizationFormId]);
    
    useEffect(() => {
        const fetchData = async () => {
            try {
                setError(null);
                setLoading(true);
                
                const [airportsResponse,
                    crewMembersResponse, 
                    travelAllowancesResponse, 
                    travelAuthorizationFormResponse
                ] = await Promise.all([
                    fetchAirports(),
                    fetchCrewMembers(),
                    fetchTravelAllowances(),
                    travelAuthorizationFormId ? fetchTravelAuthorizationForm() : Promise.resolve(null),
                ]);
                
                setAirports(airportsResponse.data);                
                setCrewMembers(crewMembersResponse.data);
                setTravelAllowances(travelAllowancesResponse.data);
                
                const formData = travelAuthorizationFormResponse?.data;                
                setFormValues({
                    crewCode: formData?.crewCode || "",
                    crewName: formData?.crewName || "",
                    jobGradeCode: formData?.jobGradeCode || "",
                    startDate: formData?.startDate ? toZonedTime(new Date(formData.startDate), 'UTC') : null,
                    endDate: formData?.endDate ? toZonedTime(new Date(formData.endDate), 'UTC') : null,
                    travelHours: formData?.travelHours || 0,
                    destination: formData?.destination || "",
                    travelZone: formData?.travelZone || "",
                    allowanceUnit: formData?.allowanceUnit || 0,
                    allowanceAmount: formData?.allowanceAmount || 0,
                    currency: formData?.currency || 'VND',
                    description: formData?.description || "",
                    status: formData?.status || "approved",
                });
                
            } catch (error) {
                setError(error);
                toast.error(error.response?.data?.message || "Failed to fetch data!");
            } finally {
                setLoading(false);
            }
        };
    
        fetchData();      
    }, [fetchAirports, 
        fetchCrewMembers, 
        fetchTravelAllowances,
        fetchTravelAuthorizationForm, 
        travelAuthorizationFormId
    ]); 
    
    function getAllowanceUnit(jobGradeCode, travelZone) {
        if (!jobGradeCode || !travelZone) return 0;

        // Tìm allowance theo travelZone
        const allowance = travelAllowances.find(
            (ta) => ta.travelAllowanceCode === travelZone
        );
        if (!allowance) return 0;

        // Tìm rule theo jobGrade
        const applicable = allowance.applicableTo.find((rule) =>
            rule.jobGrade.includes(jobGradeCode)
        );

        return applicable ? applicable.value : 0;
    }

    const checkoutSchema = yup.object().shape({
        crewCode: yup.string().required('Required'),
    });

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const payload = {
                ...values,
                startDate: values.startDate
                    ? parseUTCDateTime(values.startDate) 
                    : null,
                endDate: values.endDate
                    ? parseUTCDateTime(values.endDate) 
                    : null,
            };            
            const url = travelAuthorizationFormId 
                ? `/travelauthorizationforms/update/${travelAuthorizationFormId}` 
                : '/travelauthorizationforms/new';      
            const method = travelAuthorizationFormId ? 'put' : 'post';
            
            const response = await api({
                method,
                url,
                data: payload,
            });

            if (![200, 201].includes(response.status)) {
                toast.error("Failed to submit the form!");
                return;
            }

            toast.success(
                travelAuthorizationFormId
                    ? 'Travel authorization updated successfully.' 
                    : 'Travel authorization created successfully.'
            );        
            
            navigate("/travelauthorizationforms");
        } catch (error) {
            setError(error);
            toast.error(error.message || "Failed to submit the form!");
        }
    };


    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Travel Authorization"
                    subtitle={`${loading ? 'Loading...' : 
                        travelAuthorizationFormId 
                            ? 'Update' 
                            : 'Create'}`
                    }
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/travelauthorizationforms")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <LocalizationProvider 
                        dateAdapter={AdapterDateFns}
                        adapterLocale={locales['en-gb']}
                    >
                        <Formik
                            initialValues={formValues}
                            enableReinitialize={true}
                            validationSchema={checkoutSchema}
                            onSubmit={handleFormSubmit}
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleBlur,
                                handleChange,
                                handleSubmit,
                                setFieldValue,
                            }) => (
                                <form onSubmit={handleSubmit}>
                                    <Box
                                        display="grid"
                                        gap={2}
                                        gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                        sx={{ '& > div': { gridColumn: isDesktop ? undefined : 'span 12' } }}
                                    >                                        
                                        <Autocomplete
                                            sx={{ gridColumn: "span 6" }}
                                            options={crewMembers}
                                            getOptionLabel={(option) => `${option.crewCode} - ${option.crewName}`}
                                            value={
                                                crewMembers.find((cm) => cm.crewCode === values.crewCode) || null
                                            }
                                            onChange={(_, newValue) => {
                                                if (newValue) {
                                                    setFieldValue("crewCode", newValue.crewCode);
                                                    setFieldValue("crewName", newValue.crewName);
                                                    setFieldValue("jobGradeCode", newValue.jobGradeCode);
                                                } else {
                                                    setFieldValue("crewCode", "");
                                                    setFieldValue("crewName", "");
                                                    setFieldValue("jobGradeCode", "");
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Crew Member"
                                                    variant="filled"
                                                    required
                                                    error={!!touched.crewCode && !!errors.crewCode}
                                                    helperText={touched.crewCode && errors.crewCode}
                                                />
                                            )}
                                        />

                                        <TextField
                                            fullWidth
                                            required
                                            variant="filled"
                                            type="text"
                                            label="Job Grade Code"
                                            onBlur={handleBlur}
                                            onChange={(e) => {
                                                handleChange(e);
                                                const selectedCode = e.target.value;
                                                const unit = getAllowanceUnit(selectedCode, values.travelZone) / 24;
                                                setFieldValue("allowanceUnit", unit);

                                                const amount = (values.travelHours || 0) * unit;
                                                setFieldValue("allowanceAmount", amount);
                                            }}
                                            value={values.jobGradeCode}
                                            name="jobGradeCode"
                                            error={!!touched.jobGradeCode && !!errors.jobGradeCode}
                                            helperText={touched.jobGradeCode && errors.jobGradeCode}
                                            sx={{ gridColumn: 'span 6' }}
                                        />           

                                        <FormControl 
                                            required 
                                            variant="filled"
                                            sx={{ gridColumn: "span 6" }}
                                        >
                                            <InputLabel>Destination</InputLabel>
                                            <Select
                                                label="destination"
                                                name="destination"
                                                variant="filled"                                      
                                                value={values.destination}
                                                onChange={(e) => {
                                                    const selectedCode = e.target.value;
                                                    handleChange(e);

                                                    const selectedAirport = airports.find(ap => ap.iataCode === selectedCode);
                                                    if (selectedAirport) {
                                                        setFieldValue("travelZone", selectedAirport.travelZone || "");

                                                        const unit = getAllowanceUnit(values.jobGradeCode, selectedAirport.travelZone) / 24;
                                                        setFieldValue("allowanceUnit", unit);

                                                        const amount = (values.travelHours || 0) * unit;
                                                        setFieldValue("allowanceAmount", amount);
                                                    }
                                                }}
                                            >
                                                {airports?.map(ap => (
                                                    <MenuItem key={ap.iataCode} value={ap.iataCode}>
                                                        {ap.iataCode + ' - ' + ap.airportName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        <TextField
                                            fullWidth
                                            required
                                            variant="filled"
                                            label="Travel Zone"
                                            name="travelZone"
                                            value={values.travelZone || ""}
                                            slotProps={{
                                                input: {
                                                    readOnly: true,
                                                },
                                            }}
                                            sx={{ gridColumn: 'span 6' }}
                                        />

                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            label="Allowance Unit"
                                            name="allowanceUnit"
                                            // type="number"                                            
                                            value={formatNumber(values.allowanceUnit)}
                                            slotProps={{
                                                input: {
                                                    readOnly: true,
                                                    sx: {
                                                        "& input": { textAlign: "right" },
                                                    },
                                                },
                                            }}                                            
                                            sx={{ gridColumn: 'span 6' }}
                                        />
                                        
                                        <CustomDateTimePicker
                                            label="Start Date"
                                            name="startDate"
                                            value={values.startDate}
                                            maxDate={values.endDate}
                                            touched={touched}
                                            errors={errors}
                                            sx={{ gridColumn: "span 6" }}                                            
                                            onChange={(name, newValue) => {
                                                setFieldValue(name, newValue);
                                                if (name === "startDate" && values.endDate) {
                                                    const newHours = calcTravelHours(newValue, values.endDate);
                                                    setFieldValue("travelHours", newHours);

                                                    const unit = getAllowanceUnit(values.jobGradeCode, values.travelZone) / 24;
                                                    setFieldValue("allowanceAmount", newHours * unit);
                                                }
                                            }}
                                        />

                                        <CustomDateTimePicker
                                            label="End Date"
                                            name="endDate"
                                            value={values.endDate}
                                            minDate={values.startDate}
                                            touched={touched}
                                            errors={errors}
                                            sx={{ gridColumn: "span 6" }}
                                            onChange={(name, newValue) => {
                                                setFieldValue(name, newValue);
                                                if (name === "endDate" && values.startDate) {
                                                    const newHours = calcTravelHours(values.startDate, newValue);
                                                    setFieldValue("travelHours", newHours);

                                                    const unit = getAllowanceUnit(values.jobGradeCode, values.travelZone) / 24;
                                                    setFieldValue("allowanceAmount", newHours * unit);
                                                }
                                            }}
                                        />
                                        
                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            label="Travel Hours"
                                            name="travelHours"
                                            value={formatNumber(values.travelHours, 0, 2)}
                                            slotProps={{
                                                input: {
                                                    readOnly: true,
                                                    sx: {
                                                        "& input": { textAlign: "right" },
                                                    },
                                                },
                                            }} 
                                            sx={{ gridColumn: 'span 6' }}
                                        />

                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            // type="number"
                                            label="Allowance Amount"
                                            name="allowanceAmount"
                                            value={formatNumber(values.allowanceAmount)}                                            
                                            slotProps={{
                                                input: {
                                                    readOnly: true,
                                                    sx: {
                                                        "& input": { textAlign: "right" },
                                                    },
                                                },
                                            }}
                                            sx={{ gridColumn: "span 6" }}
                                        />

                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            label="Currency"
                                            name="currency"
                                            select
                                            value={values.currency || "VND"}
                                            onChange={(e) => setFieldValue("currency", e.target.value)}
                                            sx={{ gridColumn: "span 6" }}
                                        >
                                            {["VND", "USD", "EUR", "GBP", "AUD", "JPY", "KRW"].map((cur) => (
                                                <MenuItem key={cur} value={cur}>
                                                    {cur}
                                                </MenuItem>
                                            ))}
                                        </TextField>

                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            // multiline
                                            // rows={2}
                                            label="Description"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.description}
                                            name="description"
                                            error={!!touched.description && !!errors.description}
                                            helperText={touched.description && errors.description}
                                            sx={{ gridColumn: 'span 6' }}
                                        />

                                        <FormControl 
                                            // required 
                                            variant="filled"
                                            sx={{ gridColumn: "span 6" }}
                                        >
                                            <InputLabel>Status</InputLabel>
                                            <Select
                                                label="status"
                                                name="status"
                                                variant="filled"                                      
                                                value={values.status}
                                                onChange={handleChange}
                                            >
                                                {["pending", "approved", "rejected"].map(item => (
                                                    <MenuItem key={item} value={item}>
                                                        {item.charAt(0).toUpperCase() + item.slice(1)}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        <Box sx={{ gridColumn: "span 12", display: "flex", gap: 2 }}>
                                            {/* <Button
                                                fullWidth
                                                variant="outlined"
                                                color="primary"
                                                sx={{ height: 42 }}
                                                onClick={() => navigate("/travelauthorizationforms")}
                                            >
                                                Cancel
                                            </Button> */}
                                            <Button
                                                fullWidth
                                                type="submit"
                                                variant="contained"
                                                color="secondary"
                                                sx={{ height: 42 }}
                                            >
                                                {travelAuthorizationFormId ? "Update" : "Create"}
                                            </Button>
                                        </Box>

                                    </Box>
                                </form>
                            )}
                        </Formik>
                    </LocalizationProvider>
                </Box>
            )}
        </Box>
    );
};

export default TravelAuthorizationFormPage;
